import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Users, Plus, ChevronRight, Trophy, MessageCircle, Award, Smartphone, X, ChevronDown, ChevronUp, GraduationCap, Bell } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { getAvatarUrl } from '../utils/avatarUrl';
import { isPushSupported, subscribeToPush, isSubscribed } from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === CONFIGURAZIONE SFONDO ===
// mode: 'video' per video drone, 'image' per foto statica
// NOTA: Per usare il video, carica un file in alta risoluzione (1080p o superiore)
const BG_CONFIG = {
  mode: 'image',
  videoUrl: '',
  imageUrl: '/club-bg.jpg',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [myMatches, setMyMatches] = useState([]);
  const [openMatches, setOpenMatches] = useState([]);
  const [maestri, setMaestri] = useState([]);
  const [lessonMaestri, setLessonMaestri] = useState(new Set());
  const [rankingStatus, setRankingStatus] = useState(null);
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(() => {
    return !localStorage.getItem('ananda_install_guide_dismissed');
  });
  const [installSection, setInstallSection] = useState(null);

  const dismissInstallGuide = () => {
    localStorage.setItem('ananda_install_guide_dismissed', 'true');
    setShowInstallGuide(false);
  };

  useEffect(() => {
    fetchData();
    // Track user access
    axios.post(`${API_URL}/api/analytics/track`, {}, { withCredentials: true }).catch(() => {});
    // Check push notification status
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    if (!isPushSupported()) return;
    const subscribed = await isSubscribed();
    if (!subscribed && !localStorage.getItem('ananda_push_banner_dismissed')) {
      setShowPushBanner(true);
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const sub = await subscribeToPush();
      if (sub) {
        setShowPushBanner(false);
      }
    } catch { /* ignore */ }
    finally { setPushLoading(false); }
  };

  const dismissPushBanner = () => {
    setShowPushBanner(false);
    localStorage.setItem('ananda_push_banner_dismissed', 'true');
  };

  const fetchData = async () => {
    try {
      const [matchesRes, rankRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ranking/dynamic/status`, { withCredentials: true }).catch(() => null),
      ]);
      
      // Fetch tournaments
      try {
        const tRes = await axios.get(`${API_URL}/api/tournaments`, { withCredentials: true });
        setActiveTournaments(tRes.data.filter(t => t.status === 'registration' || t.status === 'active'));
      } catch { /* ignore */ }
      
      const allMatches = matchesRes.data;
      const mine = allMatches.filter(m => 
        m.players.some(p => p.user_id === user?.user_id) && 
        (m.status === 'open' || m.status === 'full')
      );
      const available = allMatches.filter(m => 
        m.status === 'open' && 
        !m.players.some(p => p.user_id === user?.user_id)
      );
      
      setMyMatches(mine);
      setOpenMatches(available);
      if (rankRes) setRankingStatus(rankRes.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaestri = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/maestri`, { withCredentials: true });
      setMaestri(res.data);
      const enabledSet = new Set();
      for (const m of res.data) {
        try {
          const sRes = await axios.get(`${API_URL}/api/maestri/${m.user_id}/lessons/settings`, { withCredentials: true });
          if (sRes.data.enabled) enabledSet.add(m.user_id);
        } catch { /* ignore */ }
      }
      setLessonMaestri(enabledSet);
    } catch (error) {
      console.error('Error fetching maestri:', error);
    }
  };

  useEffect(() => {
    fetchMaestri();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buongiorno';
    if (hour < 18) return 'Buon pomeriggio';
    return 'Buonasera';
  };

  const formatMatchDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEEE d MMMM', { locale: it });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-white/60">Caricamento...</div>
      </div>
    );
  }

  // Glass card style - minimal opacity to show background
  const glassCard = "bg-white/[0.02] backdrop-blur-[2px] border border-white/[0.05]";
  const glassCardHover = `${glassCard} hover:bg-white/[0.06] hover:-translate-y-0.5 transition-all`;

  return (
    <div className="relative min-h-screen animate-fadeIn" data-testid="dashboard">
      {/* Fixed Full-Screen Background */}
      <div className="fixed inset-0 -z-10">
        {BG_CONFIG.mode === 'video' && BG_CONFIG.videoUrl ? (
          <>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="bg-video"
              poster={BG_CONFIG.imageUrl}
            >
              <source src={BG_CONFIG.videoUrl} type="video/mp4" />
              <source src={BG_CONFIG.videoUrl} type="video/quicktime" />
            </video>
            {/* Fallback image if video doesn't load */}
            <img
              src={BG_CONFIG.imageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover -z-10"
            />
          </>
        ) : (
          <img
            src={BG_CONFIG.imageUrl}
            alt="Ananda Sporting Club"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="bg-image"
          />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 p-4 pt-20 sm:p-6 sm:pt-24 lg:p-8 lg:pt-24 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest mb-0.5">Ananda Sporting Club</p>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
              {getGreeting()}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-white/60 mt-0.5 text-sm">
              Pronto per una partita?
            </p>
          </div>
          <Link
            to="/matches/new"
            data-testid="create-match-btn"
            className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white rounded-full px-5 py-2.5 font-medium hover:bg-white/25 transition-all hover:-translate-y-0.5 border border-white/20"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Nuova Partita</span>
          </Link>
        </div>

        {/* Push Notifications Banner */}
        {showPushBanner && (
          <div className="relative rounded-2xl p-4 bg-sky-500/50 backdrop-blur-sm border border-sky-400/20 overflow-hidden" data-testid="push-banner">
            <button
              onClick={dismissPushBanner}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70"
              data-testid="dismiss-push-banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0 pr-6">
                <p className="text-white font-semibold text-sm">Attiva le Notifiche!</p>
                <p className="text-white/70 text-xs mt-0.5">Ricevi avvisi per nuove partite, tornei e messaggi</p>
              </div>
            </div>
            <button
              onClick={handleEnablePush}
              disabled={pushLoading}
              data-testid="enable-push-btn"
              className="mt-3 w-full py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 border border-white/15 disabled:opacity-50"
            >
              {pushLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Attiva Notifiche
                </>
              )}
            </button>
          </div>
        )}

        {/* Ranking Banner */}
        {rankingStatus && (
          <Link
            to="/regolamento"
            data-testid="ranking-dinamico-banner"
            className={`block rounded-3xl p-5 sm:p-6 relative overflow-hidden group transition-all hover:-translate-y-0.5 hover:shadow-xl ${
              rankingStatus.active
                ? 'bg-gradient-to-br from-amber-500/60 via-orange-500/60 to-rose-500/60 backdrop-blur-sm'
                : 'bg-gradient-to-br from-indigo-500/60 via-purple-500/60 to-pink-500/60 backdrop-blur-sm'
            } border border-white/10`}
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-8 -translate-x-8" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 ring-2 ring-white/10">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-base sm:text-lg">
                  {rankingStatus.active ? 'Classifica Dinamica Attiva!' : 'Classifica Dinamica'}
                </p>
                {rankingStatus.active ? (
                  <p className="text-white/80 text-sm mt-0.5">Sfida i tuoi avversari e scala la classifica</p>
                ) : (
                  <p className="text-white/80 text-sm mt-0.5">
                    Si attiva con {rankingStatus.required_male_players} iscritti — {rankingStatus.current_male_players}/{rankingStatus.required_male_players}
                  </p>
                )}
                <p className="text-white/90 text-xs mt-1.5 flex items-center gap-1 font-medium animate-pulse">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  Tocca per scoprire come funziona la classifica!
                </p>
              </div>
              {!rankingStatus.active ? (
                <div className="flex-shrink-0">
                  <div className="relative w-14 h-14">
                    <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="white" strokeWidth="3"
                        strokeDasharray={`${(rankingStatus.current_male_players / rankingStatus.required_male_players) * 88} 88`}
                        strokeLinecap="round" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">
                      {Math.round((rankingStatus.current_male_players / rankingStatus.required_male_players) * 100)}%
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <ChevronRight className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Tournament Banner */}
        {activeTournaments.length > 0 && activeTournaments.map(t => (
          <Link
            key={t.tournament_id}
            to={`/tournaments/${t.tournament_id}`}
            data-testid={`tournament-banner-${t.tournament_id}`}
            className="block rounded-2xl p-4 relative overflow-hidden group transition-all hover:-translate-y-0.5 bg-gradient-to-r from-amber-500/60 via-orange-500/60 to-red-500/60 backdrop-blur-sm border border-amber-400/20 hover:from-amber-500/70 hover:via-orange-500/70 hover:to-red-500/70"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
            <div className="relative flex items-center gap-3">
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-white/70 text-xs mt-0.5">
                  {t.gender === 'M' ? 'Maschile' : 'Femminile'} — {t.participants?.length || 0} iscritti
                  {t.status === 'registration' && ' — Iscrizioni Aperte!'}
                  {t.status === 'active' && ' — In Corso'}
                </p>
              </div>
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <ChevronRight className="w-4 h-4 text-white" />
              </div>
            </div>
          </Link>
        ))}

        {/* Prenota il Campo */}
        <a
          href="https://apps.apple.com/it/app/ananda-sporting-club/id6502181699"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="booking-reminder-banner"
          className={`block rounded-2xl p-4 relative overflow-hidden group transition-all hover:-translate-y-0.5 bg-emerald-500/50 backdrop-blur-sm border border-emerald-400/20 hover:bg-emerald-500/60`}
        >
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">Prenota il Campo</p>
              <p className="text-emerald-100 text-xs mt-0.5">Ricorda di prenotare tramite l'app Ananda Sporting Club</p>
            </div>
            <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </a>

        {/* Install App Guide */}
        {showInstallGuide && (
          <section className={`${glassCard} rounded-3xl p-5 text-white relative overflow-hidden`} data-testid="install-app-banner">
            <button
              onClick={dismissInstallGuide}
              data-testid="dismiss-install-guide"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 bg-sky-400/40 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-outfit font-semibold text-base">Installa l'App!</h3>
                <p className="text-xs text-white/60">Aggiungila alla schermata Home</p>
              </div>
            </div>

            <p className="text-sm text-white/70 mb-4">
              Puoi usare Ananda Sport come una vera app! Scegli il tuo dispositivo:
            </p>

            {/* iOS */}
            <div className="bg-white/10 rounded-2xl overflow-hidden mb-2">
              <button
                onClick={() => setInstallSection(installSection === 'ios' ? null : 'ios')}
                data-testid="install-ios-toggle"
                className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  <span className="text-sm font-medium">iPhone / iPad (Safari)</span>
                </div>
                {installSection === 'ios' ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {installSection === 'ios' && (
                <div className="px-3 pb-3 space-y-2.5" data-testid="install-ios-steps">
                  {['Apri questo sito con <strong>Safari</strong>',
                    'Tocca <strong>Condividi</strong> (il quadrato con la freccia in basso)',
                    'Tocca <strong>"Aggiungi alla schermata Home"</strong>',
                    'Conferma con <strong>"Aggiungi"</strong> in alto a destra'
                  ].map((step, i) => (
                    <div key={i} className="flex gap-2.5 items-start">
                      <div className="w-6 h-6 bg-sky-400/30 rounded-md flex items-center justify-center flex-shrink-0 text-sky-300 font-bold text-xs">{i+1}</div>
                      <p className="text-sm text-white/80" dangerouslySetInnerHTML={{ __html: step }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Android */}
            <div className="bg-white/10 rounded-2xl overflow-hidden">
              <button
                onClick={() => setInstallSection(installSection === 'android' ? null : 'android')}
                data-testid="install-android-toggle"
                className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 2.273l1.74-1.74a.5.5 0 00-.707-.707l-1.956 1.956A9.97 9.97 0 0012 .5a9.97 9.97 0 00-4.6 1.282L5.444.174a.5.5 0 00-.707.707l1.74 1.74A9.955 9.955 0 002 10v1h20v-1a9.955 9.955 0 00-4.477-7.727zM8.5 8a1 1 0 110-2 1 1 0 010 2zm7 0a1 1 0 110-2 1 1 0 010 2zM2 12v7a3 3 0 003 3h14a3 3 0 003-3v-7H2z"/></svg>
                  <span className="text-sm font-medium">Android (Chrome)</span>
                </div>
                {installSection === 'android' ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {installSection === 'android' && (
                <div className="px-3 pb-3 space-y-2.5" data-testid="install-android-steps">
                  {['Apri questo sito con <strong>Chrome</strong>',
                    'Tocca i <strong>tre puntini</strong> in alto a destra',
                    'Tocca <strong>"Aggiungi a schermata Home"</strong>',
                    'Conferma con <strong>"Aggiungi"</strong>'
                  ].map((step, i) => (
                    <div key={i} className="flex gap-2.5 items-start">
                      <div className="w-6 h-6 bg-emerald-400/30 rounded-md flex items-center justify-center flex-shrink-0 text-emerald-300 font-bold text-xs">{i+1}</div>
                      <p className="text-sm text-white/80" dangerouslySetInnerHTML={{ __html: step }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* I Nostri Maestri */}
        {maestri.length > 0 && (
          <section data-testid="maestri-section">
            <h2 className="font-outfit text-xl font-semibold text-white mb-4 flex items-center gap-2 drop-shadow">
              <Award className="w-5 h-5 text-emerald-400" />
              I Nostri Maestri
            </h2>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
              {maestri.map((maestro) => {
                const isMe = maestro.user_id === user?.user_id;
                return (
                  <div
                    key={maestro.user_id}
                    data-testid={`maestro-card-${maestro.user_id}`}
                    className={`flex-shrink-0 w-44 bg-white/[0.02] backdrop-blur-[2px] rounded-2xl p-4 snap-start hover:bg-white/[0.08] transition-all border border-white/[0.06]`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-emerald-500/40 rounded-2xl flex items-center justify-center text-white text-xl font-bold overflow-hidden mb-3 ring-2 ring-emerald-400/30">
                        {getAvatarUrl(maestro) ? (
                          <img src={getAvatarUrl(maestro)} alt={maestro.name} className="w-full h-full rounded-2xl object-cover" />
                        ) : (
                          maestro.name?.charAt(0)
                        )}
                      </div>
                      <p className="font-semibold text-white text-sm truncate w-full">
                        {maestro.name} {maestro.surname || ''}
                      </p>
                      <span className="text-xs text-emerald-300 font-medium bg-emerald-500/20 px-2.5 py-0.5 rounded-full mt-1.5">
                        Maestro
                      </span>
                      {!isMe && (
                        <div className="mt-3 w-full space-y-1.5">
                          {lessonMaestri.has(maestro.user_id) && (
                            <Link
                              to={`/maestri/${maestro.user_id}/prenota`}
                              data-testid={`book-lesson-${maestro.user_id}`}
                              className="w-full flex items-center justify-center gap-1.5 py-2 bg-emerald-500/50 text-white rounded-xl text-xs font-semibold hover:bg-emerald-500/70 transition-colors border border-emerald-400/30"
                            >
                              <GraduationCap className="w-3.5 h-3.5" />
                              Prenota Lezione
                            </Link>
                          )}
                          <Link
                            to={`/chat?user=${maestro.user_id}`}
                            data-testid={`chat-maestro-${maestro.user_id}`}
                            className="w-full flex items-center justify-center gap-1.5 py-2 bg-sky-500/40 text-white rounded-xl text-xs font-semibold hover:bg-sky-500/60 transition-colors border border-sky-400/25"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            Scrivi
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* My Matches Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-xl font-semibold text-white drop-shadow">
              Le Mie Partite
            </h2>
            {myMatches.length > 0 && (
              <Link to="/matches" className="text-sky-300 text-sm font-medium hover:text-sky-200 flex items-center gap-1">
                Vedi tutte <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {myMatches.length === 0 ? (
            <div className={`${glassCard} rounded-3xl p-8 text-center`}>
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-white/50" />
              </div>
              <h3 className="font-outfit text-lg font-medium text-white mb-2">
                Nessuna partita programmata
              </h3>
              <p className="text-white/50 mb-4">
                Crea una nuova partita o unisciti a una esistente
              </p>
              <Link
                to="/matches/new"
                className="inline-flex items-center gap-2 bg-sky-500/70 backdrop-blur-sm text-white rounded-full px-5 py-2.5 font-medium hover:bg-sky-500/90 transition-all border border-sky-400/30"
              >
                <Plus className="w-5 h-5" />
                Crea Partita
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {myMatches.slice(0, 3).map((match, index) => (
                <Link
                  key={match.match_id}
                  to={`/matches/${match.match_id}`}
                  data-testid={`my-match-card-${index}`}
                  className={`${glassCardHover} rounded-3xl p-6`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-sky-500/30 rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-sky-300" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60 capitalize">
                        {formatMatchDate(match.date)}
                      </p>
                      <p className="font-semibold text-white flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {match.time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-white/40" />
                    <span className="text-sm text-white/70">
                      {match.players.length}/{match.max_players} giocatori
                    </span>
                    <span className={`ml-auto text-xs font-medium px-2 py-1 rounded-full ${
                      match.status === 'open' 
                        ? 'bg-green-500/30 text-green-300' 
                        : match.status === 'full'
                        ? 'bg-sky-500/30 text-sky-300'
                        : 'bg-white/10 text-white/60'
                    }`}>
                      {match.status === 'open' ? 'Aperta' : match.status === 'full' ? 'Completa' : match.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Available Matches Section */}
        {openMatches.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-xl font-semibold text-white drop-shadow">
                Partite Disponibili
              </h2>
              <Link to="/matches" className="text-sky-300 text-sm font-medium hover:text-sky-200 flex items-center gap-1">
                Vedi tutte <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {openMatches.slice(0, 3).map((match, index) => (
                <Link
                  key={match.match_id}
                  to={`/matches/${match.match_id}`}
                  data-testid={`open-match-card-${index}`}
                  className={`${glassCardHover} rounded-3xl p-6`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-500/30 rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-amber-300" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60 capitalize">
                        {formatMatchDate(match.date)}
                      </p>
                      <p className="font-semibold text-white flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {match.time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-sky-400/40 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {match.creator_name?.charAt(0)}
                      </div>
                      <span className="text-sm text-white/70">
                        {match.creator_name}
                      </span>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-500/30 text-green-300">
                      {match.players.length}/{match.max_players}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Quick Actions */}
        <section className="grid grid-cols-2 sm:grid-cols-6 gap-3 pb-16">
          {[
            { to: '/matches', testid: 'quick-matches', icon: <Calendar className="w-6 h-6 text-sky-300" />, label: 'Partite', bg: 'bg-sky-500/25' },
            { to: '/tournaments', testid: 'quick-tournaments', icon: <Trophy className="w-6 h-6 text-orange-300" />, label: 'Tornei', bg: 'bg-orange-500/25' },
            { to: '/lessons/my', testid: 'quick-lessons', icon: <GraduationCap className="w-6 h-6 text-emerald-300" />, label: 'Lezioni', bg: 'bg-emerald-500/25' },
            { to: '/ranking', testid: 'quick-ranking', icon: <Trophy className="w-6 h-6 text-amber-300" />, label: 'Classifica', bg: 'bg-amber-500/25' },
            { to: '/chat', testid: 'quick-chat', icon: <MessageCircle className="w-6 h-6 text-purple-300" />, label: 'Chat', bg: 'bg-purple-500/25' },
            { to: '/profile', testid: 'quick-profile', icon: (
              <svg className="w-6 h-6 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ), label: 'Profilo', bg: 'bg-green-500/25' },
          ].map((item) => (
            <Link
              key={item.testid}
              to={item.to}
              data-testid={item.testid}
              className={`${glassCardHover} rounded-2xl p-4 text-center`}
            >
              <div className={`w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                {item.icon}
              </div>
              <span className="text-sm font-medium text-white/80">{item.label}</span>
            </Link>
          ))}
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
